<template>
  <v-container id="sub-orders-table" fluid>
    <v-card :disabled="loadingData" outlined>
      <v-data-table
        :footer-props="footerProps"
        :header-props="headerProps"
        :headers="computedHeaders"
        :items="computedSubOrders"
        :loading="loadingData"
        :loading-text="$t('order.ordersLoading')"
        :no-data-text="$t('order.noOrders')"
        :options.sync="options"
        :server-items-length="totalOrders"
        dense
        item-key="id"
        mobile-breakpoint="650"
      >
        <template v-slot:item.action="{ item }">
          <v-btn :class="[!isMobile ? '' : 'pr-0']" color="primary" text @click="executeAction(item.order_id)">
            <v-icon v-if="$vuetify.breakpoint.width >= 650 && $vuetify.breakpoint.width <= 768">mdi-magnify</v-icon>
            <span v-else>{{ item.action }}</span>
          </v-btn>
        </template>

        <template v-slot:item.date="{ item }">
          <span>{{ new Date(item.date) | moment('DD/MM/YYYY HH:mm:ss') }}</span>
        </template>

        <template v-slot:item.current_status="{ item }">
          <span>{{ getStatusTranslation(item.current_status) }}</span>
        </template>

        <template v-slot:item.type="{ item }">
          <span>{{ getTypeTranslation(item.type) }}</span>
        </template>

        <template v-slot:item.order_id="{ item }">
          <p :style="orderIdColumn" class="mb-0">{{ item.order_id }}</p>
        </template>

        <template v-slot:item.pickup_deadline="{ item }">
          <span v-if="item.remainingTime != undefined && !isNaN(item.remainingTime) && displayRemainingTime(item)">
            <span v-if="item.remainingTime <= 0" class="red--text">{{ $t('subOrder.timeout') }}</span>
            <countdown v-else :key="item.order_id" :time="item.remainingTime" :transform="transform">
              <template slot-scope="props">
                <span
                  v-if="props.days == 0 && props.hours == 0"
                  :class="props.minutes < 15 ? 'red--text' : 'orange--text'"
                >
                  {{ props.minutes }} min {{ props.seconds }}s
                </span>
                <span v-else>
                  <span v-if="props.days > 0">{{ props.days }} j</span>
                  {{ props.hours }} h {{ props.minutes }}
                </span>
              </template>
            </countdown>
          </span>
          <span v-else class="mdi mdi-infinity"></span>
        </template>

        <template v-slot:top>
          <v-toolbar flat>
            <v-text-field
              v-if="!isMobile"
              v-model="searchOrderId"
              :placeholder="$t('order.numOrder').toString()"
              outlined
              dense
              clearable
              hide-details
              prepend-inner-icon="mdi-magnify"
              style="max-width: 250px"
              class="mr-2"
              @click:clear="clearOrderSearch"
            />
            <v-badge :content="nbActiveFilters" :value="nbActiveFilters" overlap>
              <v-btn color="primary" outlined @click="displayFilters = !displayFilters">
                <v-icon>mdi-filter-plus-outline</v-icon>
              </v-btn>
            </v-badge>
            <v-spacer></v-spacer>
            <v-btn color="primary" outlined @click="reloadList()">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </v-toolbar>
          <v-row v-if="isMobile" class="mx-0">
            <v-col cols="12">
              <v-text-field
                v-model="searchOrderId"
                :placeholder="$t('order.numOrder').toString()"
                outlined
                dense
                clearable
                hide-details
                prepend-inner-icon="mdi-magnify"
                @click:clear="clearOrderSearch"
              />
            </v-col>
          </v-row>
        </template>
      </v-data-table>
      <filter-dialog
        :key="formFiltersKey"
        v-model="displayFilters"
        :fields="formFilter"
        :loadingData="loadingData"
        :title="$t('order.filterOrders')"
        @confirm="filter"
      ></filter-dialog>
    </v-card>
  </v-container>
</template>

<script>
  import _ from 'lodash'
  import { mapState, mapMutations } from 'vuex'
  import { EventBus } from '../../event-bus'
  import { NONE_FILTER } from '@/constants/filters'
  import notSetOptionMixin from '@/mixins/notSetOption'

  export default {
    name: 'OrdersTable',
    mixins: [notSetOptionMixin],
    data() {
      return {
        subOrders: [],
        totalOrders: 0,
        options: {
          itemsPerPage: 15,
          sortDesc: [], // fix the default value change in new vuetify version
        },
        loadingData: false,
        displayFilters: false,
        filters: [],
        formFiltersKey: Date.now(),
        componentPreviousPath: '',
        orderSettings: {},
        serviceActivation: {},
        displayFieldSettings: null,
        searchOrderId: '',
        segmentFilterDescriptor: [],
      }
    },
    async mounted() {
      if (this.currentStoreDetails && this.currentStoreDetails.address && this.currentStoreDetails.address.country_id) {
        await this.getSettingsByCountry(this.currentStoreDetails.address.country_id)
      }
    },
    props: {
      // Legacy mode only: not provided by segment-driven routes.
      status: {
        type: Array,
        required: false,
        default: () => {
          return []
        },
      },
      types: {
        type: Array,
        required: false,
        default: () => {
          return []
        },
      },
      carrierTypes: {
        type: Array,
        required: false,
        default: () => {
          return []
        },
      },
      restrictToCurrentStore: {
        type: Boolean,
        required: false,
        default: false,
      },
      // Name of the configured view to render (e.g. 'store_orders'). Empty = legacy route-driven mode.
      // Note: a segment route still relies on `status` for two row-level concerns outside the filter modal —
      // the per-row action label (`compute`) and the mobile status column (`headersMobile`). Both degrade to
      // the default when `status` is empty, so passing it stays optional.
      segment: {
        type: String,
        required: false,
        default: '',
      },
    },
    watch: {
      searchOrderId: _.debounce(function () {
        this.reloadList()
      }, 500),
      options: {
        handler() {
          this.list()
        },
      },
      $props: {
        // any change on props reload datas, use separate watcher for each prop did not work (double event)
        handler() {
          // Legacy mode derives the filter options from the route props; segment mode gets them from the API.
          if (!this.isSegmentMode) {
            let translatedStatus = []
            for (let sts of this.status) {
              translatedStatus.push(
                this.statusTranslations.find((statusTranslation) => statusTranslation.value === sts)
              )
            }
            this.formFilter.find((header) => header.value === 'current_status').arrayValues = translatedStatus

            const translatedCarrierTypes = this.carrierTypes
              .map((typeValue) => this.carrierTypeTranslations.find((carrierType) => carrierType.value === typeValue))
              .filter(Boolean)
            this.formFilter.find((header) => header.value === 'carrier_type').arrayValues = translatedCarrierTypes

            const translatedTypes = this.types
              .map((typeValue) => this.typeTranslations.find((type) => type.value === typeValue))
              .filter(Boolean)
            this.formFilter.find((header) => header.value === 'type').arrayValues = translatedTypes
          }

          this.subOrders = []
          this.formFiltersKey = Date.now()
          this.filters = []
          if (['orders'].includes(this.$store.state.previousPath.split('/')[1])) {
            this.list()
          }
        },
        deep: true,
        immediate: true,
      },
    },
    activated() {
      let previousPath = this.$store.state.previousPath.split('/')[1]
      // we reset filters if the previous page wasn't the details of an order of the table OR if vue recycled it from a different instance
      if (
        previousPath !== 'seller-orders' ||
        (previousPath === 'orders' && this.componentPreviousPath !== this.$route.path)
      ) {
        this.formFiltersKey = Date.now()
        this.filters = []
        this.options.itemsPerPage = 15
        this.options.page = 1
        this.options.sortBy = []
        this.options.sortDesc = []
        this.searchOrderId = ''
      }
      this.subOrders = []
      this.list()
    },
    computed: {
      isMobile() {
        return this.$vuetify.breakpoint.width < 650
      },
      isSegmentMode() {
        return !!this.segment
      },
      ...mapState(['loading', 'currentStore', 'isCustomerService', 'currentStoreDetails', 'roles']),
      computedSubOrders: function () {
        let computedSubOrders = this.subOrders
        computedSubOrders = computedSubOrders.map((subOrder) => this.compute(subOrder))
        return computedSubOrders
      },
      nbActiveFilters: function () {
        const count = this.filters.filter((filter) => filter.active).length
        return this.searchOrderId && this.searchOrderId.trim() !== '' ? count + 1 : count
      },
      computedHeaders() {
        if (this.$vuetify.breakpoint.width < 769) {
          return this.headersMobile
        } else {
          return this.headers
        }
      },
      statusTranslations() {
        return [
          { text: this.$t('order.waiting'), value: 'waiting' },
          { text: this.$t('order.waitingPayment'), value: 'waiting_payment' },
          { text: this.$t('order.confirmed'), value: 'confirmed' },
          { text: this.$t('order.partiallyConfirmed'), value: 'partially_confirmed' },
          { text: this.$t('order.toPickup'), value: 'to_pickup' },
          { text: this.$t('order.waitingCarrier'), value: 'waiting_carrier' },
          { text: this.$t('order.shippingInProgress'), value: 'shipping_in_progress' },
          { text: this.$t('order.partialShippingInProgress'), value: 'partial_shipping_in_progress' },
          { text: this.$t('order.delivered'), value: 'delivered' },
          { text: this.$t('order.deliveredPartially'), value: 'partially_delivered' },
          { text: this.$t('order.cancelled'), value: 'cancelled' },
          { text: this.$t('subOrder.waitingAllocation'), value: 'waiting_allocation' },
          { text: this.$t('order.blocked'), value: 'blocked' },
          { text: this.$t('order.error'), value: 'error' },
        ]
      },
      carrierTypeTranslations() {
        return [
          { text: this.$t('order.homeDelivery'), value: 'home_delivery' },
          { text: this.$t('order.storeDelivery'), value: 'store_delivery' },
          { text: this.$t('order.relayPointDelivery'), value: 'relay_point_delivery' },
          { text: this.$t('order.expressHomeDelivery'), value: 'express_home_delivery' },
          this.notSetOption,
        ]
      },
      typeTranslations() {
        return [
          { text: this.$t('subOrder.clickAndReserve'), value: 'click_and_reserve' },
          { text: this.$t('subOrder.interStore'), value: 'inter_store' },
          { text: this.$t('subOrder.shipFromWarehouse'), value: 'ship_from_warehouse' },
          { text: this.$t('subOrder.clickAndCollect'), value: 'click_and_collect' },
          { text: this.$t('subOrder.inStorePickup'), value: 'in_store_pickup' },
          { text: this.$t('subOrder.shipFromStore'), value: 'ship_from_store' },
          this.notSetOption,
        ]
      },
      allTypeTranslations() {
        return [...this.carrierTypeTranslations, ...this.typeTranslations]
      },
      formFilter() {
        return this.isSegmentMode ? this.segmentFormFilter : this.legacyFormFilter
      },
      // Single source of truth for the filter modal section titles, shared by both modes.
      filterFieldLabels() {
        return {
          order_id: this.$t('order.numOrder'),
          carrier_type: this.$t('order.carrierType'),
          type: this.$t('order.type'),
          origin: this.$t('order.origin'),
          current_status: this.$t('order.status'),
          date: this.$t('order.date'),
          customer: this.$t('order.client'),
          'shipping_address.last_name': this.$t('subOrder.lastNameShipping'),
          'shipping_address.first_name': this.$t('subOrder.firstNameShipping'),
        }
      },
      // Segment mode: the API describes the filterable fields and their allowed values; we only add i18n labels.
      segmentFormFilter() {
        return this.segmentFilterDescriptor.map((descriptor) => {
          const field = {
            text: this.filterLabel(descriptor.value),
            value: descriptor.value,
            filterType: descriptor.filterType,
          }
          if (descriptor.filterType === 'array') {
            const optionValues = descriptor.arrayValues || []
            field.arrayValues = optionValues.map((optionValue) => ({
              value: optionValue,
              text: this.filterOptionLabel(descriptor.value, optionValue),
            }))
          }
          if (descriptor.value === 'order_id') {
            field.initialValue = this.searchOrderId || ''
          }
          return field
        })
      },
      // Legacy mode: the filterable fields and their values are derived from the route props.
      legacyFormFilter() {
        let headers = [
          {
            text: this.filterLabel('order_id'),
            value: 'order_id',
            filterType: 'text',
            initialValue: this.searchOrderId || '',
          },
          {
            text: this.filterLabel('carrier_type'),
            value: 'carrier_type',
            filterType: 'array',
            arrayValues: this.carrierTypeTranslations.filter(
              (type) => type.value === NONE_FILTER || this.carrierTypes.includes(type.value)
            ),
          },
          {
            text: this.filterLabel('type'),
            value: 'type',
            filterType: 'array',
            arrayValues: this.typeTranslations.filter(
              (type) => type.value === NONE_FILTER || this.types.includes(type.value)
            ),
          },
          {
            text: this.filterLabel('origin'),
            value: 'origin',
            filterType: 'array',
            arrayValues: this.origin,
          },
          {
            text: this.filterLabel('current_status'),
            value: 'current_status',
            filterType: 'array',
            arrayValues: this.statusTranslations.filter((status) => this.status.includes(status.value)),
          },
          {
            text: this.filterLabel('date'),
            value: 'date',
            filterType: 'date',
          },
        ]
        if (
          this.roles &&
          this.roles.length &&
          this.addToHeaders(this.displayFieldSettings, 'customer_name', this.roles[0])
        ) {
          headers.push(
            {
              text: this.filterLabel('customer'),
              value: 'customer',
              filterType: 'text',
            },
            {
              text: this.filterLabel('shipping_address.last_name'),
              value: 'shipping_address.last_name',
              filterType: 'text',
            },
            {
              text: this.filterLabel('shipping_address.first_name'),
              value: 'shipping_address.first_name',
              filterType: 'text',
            }
          )
        }
        return headers
      },
      headers() {
        let headers = [
          {
            text: this.$t('order.numOrder'),
            value: 'order_id',
            filterType: 'text',
          },
          {
            text: this.$t('order.type'),
            value: 'type',
            filterType: 'array',
            arrayValues: this.allTypeTranslations,
          },
          {
            text: this.$t('order.origin'),
            value: 'origin',
          },
          {
            text: this.$t('order.status'),
            value: 'current_status',
            filterType: 'array',
            arrayValues: this.statusTranslations,
          },
          // {
          //   text: "ORIGINE",
          //   value: "location_id"
          // },
          {
            text: this.$t('order.date'),
            value: 'date',
            filterType: 'date',
          },
          // {
          //   text: this.$t('order.location'),
          //   value: "location"
          // },
        ]
        if (
          !this.serviceActivation.activatePickupTimeout &&
          !this.serviceActivation.activateClickAndReservePickupTimeout
        ) {
          headers = headers.filter((header) => header.value !== 'pickup_deadline')
        }

        if (
          this.roles &&
          this.roles.length &&
          this.addToHeaders(this.displayFieldSettings, 'customer_name', this.roles[0])
        ) {
          headers.push({
            text: this.$t('order.client'),
            value: 'customer',
            filterType: 'text',
          })
        }

        headers.push(
          {
            text: this.$t('order.products'),
            value: 'total_nb_product',
            sortable: false,
          },
          {
            text: this.$t('subOrder.remainingTime'),
            value: 'pickup_deadline',
          },
          {
            text: '',
            value: 'action',
            sortable: false,
          }
        )
        return headers
      },
      headersMobile() {
        let headers = [
          {
            text: this.$t('order.numOrder'),
            value: 'order_id',
            filterType: 'text',
          },
          {
            text: this.$t('order.origin'),
            value: 'origin',
          },
          {
            text: this.$t('order.status'),
            value: 'current_status',
            filterType: 'array',
            arrayValues: this.statusTranslations.filter((status) => this.status.includes(status.value)),
          },
          {
            text: this.$t('order.date'),
            value: 'date',
            filterType: 'date',
          },
        ]
        if (
          this.roles &&
          this.roles.length &&
          this.addToHeaders(this.displayFieldSettings, 'customer_name', this.roles[0])
        ) {
          headers.push({
            text: this.$t('order.client'),
            value: 'customer',
            filterType: 'text',
          })
        }
        headers.push(
          {
            text: this.$t('order.products'),
            value: 'total_nb_product',
            sortable: false,
          },
          {
            text: '',
            value: 'action',
            sortable: false,
          }
        )
        return headers
      },
      actions() {
        return {
          waiting: this.$t('subOrder.prepare'),
          confirmed: this.$t('subOrder.resume'),
        }
      },
      footerProps() {
        return {
          'items-per-page-options': [15, 30, 50],
          'items-per-page-text': this.$t('order.ordersPerPage'),
        }
      },
      headerProps() {
        return {
          sortByText: this.$t('order.sortByText'),
        }
      },
      origin() {
        return [
          { text: this.$t('order.web'), value: 'web' },
          { text: this.$t('order.shop'), value: 'shop' },
          { text: this.$t('order.marketplace'), value: 'marketplace' },
        ]
      },
      orderIdColumn() {
        if (this.$vuetify.breakpoint.width < 450) {
          return 'max-width: 150px'
        }
        if (this.$vuetify.breakpoint.width < 650) {
          return 'max-width: 250px'
        }
        if (this.$vuetify.breakpoint.width < 768) {
          return 'max-width: 100px'
        }
        if (this.$vuetify.breakpoint.width === 768) {
          return 'max-width: 140px'
        }
        if (this.$vuetify.breakpoint.width < 1264) {
          return 'max-width: 250px'
        } else {
          return 'max-width: 300px'
        }
      },
    },
    methods: {
      compute: function (value) {
        let totalNbProduct = 0
        if (['waiting', 'waiting_payment'].includes(value.current_status)) {
          for (let item of value.items) {
            totalNbProduct += item.product_quantity
          }
        } else {
          for (let item of value.items) {
            if (item.validated_quantity) {
              totalNbProduct += item.validated_quantity
            }
          }
        }
        let customer = value.customer.first_name + ' ' + value.customer.last_name

        if (value.shipping_address && this.$route.name === 'waitingCustomerOrders') {
          customer = value.shipping_address.first_name + ' ' + value.shipping_address.last_name
        }

        let computedSubOrder = {
          _id: value._id,
          sub_order_id: value.sub_order_id,
          order_id: value.order_id,
          origin: value.origin,
          type: value.type ? value.type : value.carrier.carrier_type,
          location_id: value.location_id,
          date: value.date,
          customer: customer,
          missing_date: new Date().toISOString(),
          current_status: value.current_status,
          total_nb_product: totalNbProduct,
          items: value.items,
          // location: value.source_location_id,
          action: this.actions[this.status[0]] ? this.actions[this.status[0]] : this.$t('order.viewDetails'),
        }
        if (value.current_status === 'to_pickup') {
          computedSubOrder.remainingTime = new Date(value.pickup_deadline) - new Date()
        }
        return computedSubOrder
      },
      ...mapMutations({
        setLoading: 'SET_LOADING',
      }),
      executeAction: function (orderId) {
        this.$router.push('/seller-orders/' + orderId)
      },
      list() {
        this.loadingData = true
        let { page, itemsPerPage, sortDesc, sortBy } = this.options
        const queryStringParameters = {
          page: page,
          itemsPerPage: itemsPerPage,
          locationId: this.restrictToCurrentStore ? this.currentStore : undefined,
          sortBy: sortBy.length === 0 ? 'date' : sortBy,
          sortDesc: sortDesc.length === 0 ? true : sortDesc,
        }
        // The user-selected filters flow the same way in both modes.
        this.applyUserFilters(queryStringParameters)
        if (this.isSegmentMode) {
          // The configured view is the base scope; no default status/type/carrier scoping is sent.
          queryStringParameters.segment = this.segment
        } else {
          this.applyLegacyScoping(queryStringParameters)
        }
        const myInit = {
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
          queryStringParameters,
        }
        this.$Amplify.API.get('orders', '/v1/int/orders', myInit)
          .then((res) => {
            this.subOrders = res.data
            // /v1/int/orders returns nbOrders since RTLOMS-603; nbSubOrders remains on the segment-mode empty response.
            this.totalOrders = res.nbOrders ?? res.nbSubOrders ?? 0
            this.loadingData = false
            if (this.isSegmentMode) {
              const descriptor = res.filters || []
              // The descriptor arrives asynchronously; re-key the (mount-time built) filter dialog when it changes.
              if (JSON.stringify(descriptor) !== JSON.stringify(this.segmentFilterDescriptor)) {
                this.segmentFilterDescriptor = descriptor
                this.formFiltersKey = Date.now()
              }
            }
            if (this.totalOrders < (this.options.page - 1) * this.options.itemsPerPage + 1) {
              let pageNumber = Math.floor(this.totalOrders / this.options.itemsPerPage)
              this.options.page = pageNumber === 0 ? 1 : pageNumber
            }
          })
          .catch((e) => {
            this.loadingData = false
            // Segment mode fails deterministically on a bad visibility config (invalid_segment,
            // segment_has_no_usable_block): log the back message, it points at the configuration, not the network.
            console.error('orders loading failed:', e.response?.data?.message || e.message, e)
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: this.$t('subOrder.ordersLoadingErrorMessage'),
              color: 'error',
            })
          })
      },
      // Common to both modes: translates the user-selected filter modal into query-string parameters.
      applyUserFilters(queryStringParameters) {
        const activeCarrierFilter = this.filters.find(
          (filter) => filter.name === 'carrier_type' && filter.active && filter.value.length !== 0
        )
        const activeTypeFilter = this.filters.find(
          (filter) => filter.name === 'type' && filter.active && filter.value.length !== 0
        )
        if (activeCarrierFilter) {
          queryStringParameters.carrier_types = activeCarrierFilter.value
        }
        if (activeTypeFilter) {
          queryStringParameters.types = activeTypeFilter.value
        }
        let filters = {}
        for (let filter of this.filters) {
          if (filter.active) {
            if (['carrier_type', 'type'].includes(filter.name)) {
              // already handled above
            } else if (filter.name === 'current_status' && filter.value.length !== 0) {
              queryStringParameters.status = filter.value
            } else if (filter.name === 'origin' && filter.value.length !== 0) {
              queryStringParameters.origin = filter.value
            } else {
              filters[filter.name] = filter.value
            }
          }
        }
        if (this.searchOrderId && this.searchOrderId.trim() !== '') {
          filters['order_id'] = this.searchOrderId.trim()
        }
        if (Object.entries(filters).length !== 0) {
          queryStringParameters.filters = JSON.stringify(filters)
        }
      },
      // Legacy mode only: default scoping derived from the route props, used when no user filter overrides it.
      applyLegacyScoping(queryStringParameters) {
        // The flag qualifies the screen, not the user selection: it must survive a status filter.
        if (this.status.length === 1 && this.status.includes('waiting_payment')) {
          queryStringParameters.payment_validation_required = true
        }
        if (this.status.length && queryStringParameters.status === undefined) {
          queryStringParameters.status = this.status
        }
        // A user filter on either field drops both defaults, as it did before the segment refactor.
        if (queryStringParameters.carrier_types === undefined && queryStringParameters.types === undefined) {
          if (this.carrierTypes.length) {
            queryStringParameters.carrier_types = this.carrierTypes
          }
          if (this.types.length) {
            queryStringParameters.types = this.types
          }
        }
      },
      async reloadList() {
        await this.$auth.isAuthenticated()
        EventBus.$emit('reLoadCounter')
        this.list()
      },
      clearOrderSearch() {
        this.searchOrderId = ''
        this.reloadList()
      },
      transform(props) {
        Object.entries(props).forEach(([key, value]) => {
          // Adds leading zero
          const digits = value < 10 ? `0${value}` : value

          props[key] = `${digits}`
        })
        return props
      },
      getStatusTranslation: function (statusValue) {
        let status = this.statusTranslations.find((status) => status.value === statusValue)
        if (status) {
          return status.text
        } else return statusValue
      },
      getTypeTranslation: function (typeValue) {
        let type = this.allTypeTranslations.find((type) => type.value === typeValue)
        if (type) {
          return type.text
        } else return typeValue
      },
      // i18n label for a filter field, falling back to the raw key for a field we do not label yet.
      filterLabel(fieldValue) {
        return this.filterFieldLabels[fieldValue] || fieldValue
      },
      // Segment mode: i18n label for one allowed value of a descriptor array field.
      filterOptionLabel(fieldValue, optionValue) {
        if (fieldValue === 'current_status') {
          return this.getStatusTranslation(optionValue)
        }
        if (['carrier_type', 'type'].includes(fieldValue)) {
          return this.getTypeTranslation(optionValue)
        }
        if (fieldValue === 'origin') {
          const originOption = this.origin.find((option) => option.value === optionValue)
          return originOption ? originOption.text : optionValue
        }
        return optionValue
      },
      filter(filters) {
        const orderIdFilter = filters.find((f) => f.name === 'order_id')
        if (orderIdFilter) {
          this.searchOrderId = orderIdFilter.active ? orderIdFilter.value : ''
          orderIdFilter.active = false
        }
        this.filters = filters
        this.reloadList()
      },
      async getSettingsByCountry(countryIso3) {
        // TODO REFACTOR WHEN MR WITH DEFAULT SETTINGS IS MERGED
        this.loadingData = true
        const myInit = {
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
        }

        try {
          const res = await this.$Amplify.API.get('settings', '/v1/settings/' + countryIso3, myInit)
          this.orderSettings = res

          const delaysSettings = this.orderSettings?.delays
          if (delaysSettings) {
            this.serviceActivation.activatePickupTimeout = delaysSettings?.global?.pickup_timeout?.activate
            this.serviceActivation.activateClickAndReservePickupTimeout =
              delaysSettings?.click_and_reserve?.pickup_timeout?.activate
          }

          this.displayFieldSettings = res.display?.customer_information
          this.formFiltersKey = Date.now()
        } catch (e) {
          console.log(e)
          if (e.response?.status === 404 && countryIso3 !== 'FRA') {
            console.log(`No settings for country ${this.currentStoreDetails.address.country_id}`)
            await this.getSettingsByCountry('FRA')
          }
        } finally {
          this.loadingData = false
        }
      },
      displayRemainingTime(order) {
        if (this.serviceActivation) {
          if (order.type === 'click_and_reserve') {
            return this.serviceActivation.activateClickAndReservePickupTimeout
          } else {
            return this.serviceActivation.activatePickupTimeout
          }
        }
        return false
      },
    },
    // we save the current path, to have it when this component will be reused later
    beforeRouteLeave(to, from, next) {
      this.componentPreviousPath = from.path
      next()
    },
  }
</script>
